import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Lightbox from '../components/Lightbox'
import SubpageHero from '../components/SubpageHero'
import useMeta from '../hooks/useMeta'

export default function GalleryPage() {
  useMeta('Galleri', 'Se billeder af vores professionelle rengøringsarbejde i private hjem, kontorer og luksuslejligheder. Oplev den ægte Matheus Cleaning Service kvalitet.')

  const [galleries, setGalleries] = useState([])
  const [activeTab, setActiveTab] = useState('alle')
  const [lightboxState, setLightboxState] = useState({ isOpen: false, images: [], index: 0 })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/galleri')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setGalleries(data)
        }
        setLoading(false)
      })
      .catch(() => {
        // Fallback galleries on error
        setGalleries([
          {
            title: { da: 'Privat rengøring' },
            gallery_slug: 'privat-rengoering',
            images: [
              'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/residential-cleaning-living-room.jpg',
              'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/kitchen-cleaning-service.jpg',
              'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/bathroom-cleaning-service.jpg'
            ],
            description: { da: 'Se eksempler på vores professionelle private rengøring i danske hjem.' }
          },
          {
            title: { da: 'Housekeeping' },
            gallery_slug: 'housekeeping',
            images: [
              'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/housekeeping-hotel-apartment.jpg',
              'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/residential-cleaning-living-room.jpg'
            ],
            description: { da: 'Eksklusiv housekeeping og ejendomsservice for private hjem.' }
          },
          {
            title: { da: 'Erhvervsrengøring' },
            gallery_slug: 'erhvervsrengoering',
            images: [
              'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/professional-cleaning-staff-office-no-logo.jpg',
              'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/kitchen-cleaning-service.jpg'
            ],
            description: { da: 'Professionel erhvervsrengøring af kontorer og virksomhedslokaler.' }
          },
          {
            title: { da: 'Før- og efterbilleder' },
            gallery_slug: 'foer-og-efter',
            images: [
              'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/before-after-kitchen-before.jpg',
              'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/before-after-kitchen-after.jpg',
              'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/before-after-bathroom-before.jpg',
              'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/before-after-bathroom-after.jpg'
            ],
            description: { da: 'Se de fantastiske resultater af vores grundige dybderengøring.' }
          }
        ])
        setLoading(false)
      })
  }, [])

  const tabs = [
    { id: 'alle', label: 'Alle billeder' },
    { id: 'privat-rengoering', label: 'Privat rengøring' },
    { id: 'housekeeping', label: 'Housekeeping' },
    { id: 'erhvervsrengoering', label: 'Erhvervsrengøring' },
    { id: 'foer-og-efter', label: 'Før & Efter' },
  ]

  // Get filtered images
  const getFilteredImages = () => {
    if (activeTab === 'alle') {
      return galleries.flatMap((g) => g.images || [])
    }
    const gallery = galleries.find((g) => g.gallery_slug === activeTab)
    return gallery ? gallery.images || [] : []
  }

  const handleImageClick = (imgUrl) => {
    const images = getFilteredImages()
    const index = images.indexOf(imgUrl)
    setLightboxState({
      isOpen: true,
      images,
      index: index >= 0 ? index : 0,
    })
  }

  const heroImages = [
    'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/scandinavian-luxury-living-room-clean.jpg',
    'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/kitchen-cleaning-service.jpg',
    'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/bathroom-cleaning-service.jpg'
  ]

  return (
    <div className="flex flex-col min-h-screen bg-brand-light">
      {/* Hero Section */}
      <SubpageHero
        title="Vores arbejde i billeder"
        intro="Gå på opdagelse i vores galleri og se eksempler på vores professionelle rengøring og fantastiske resultater."
        images={heroImages}
      />

      {/* Gallery Tabs & Grid */}
      <section className="py-16 flex-grow">
        <div className="mx-auto max-w-7xl px-6">
          {/* Tabs */}
          <div className="flex flex-wrap items-center justify-center gap-2 border-b border-brand-border pb-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`rounded-none px-5 py-2.5 text-sm font-semibold transition-all duration-200 focus:outline-none ${
                  activeTab === tab.id
                    ? 'bg-brand-navy text-white shadow-md'
                    : 'bg-white text-brand-text-dark border border-brand-border hover:bg-brand-gray'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="py-24 text-center">
              <i className="fa-solid fa-spinner animate-spin text-4xl text-black"></i>
              <p className="mt-4 text-sm text-brand-text-muted font-medium">Henter galleribilleder...</p>
            </div>
          ) : (
            <>
              {/* Gallery Grid */}
              <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {getFilteredImages().map((img, index) => (
                  <div
                    key={index}
                    onClick={() => handleImageClick(img)}
                    className="group relative overflow-hidden border border-brand-border bg-white shadow-sm cursor-pointer h-64 md:h-72 animate-in fade-in duration-300"
                  >
                    <img
                      src={img}
                      alt={`Galleribillede ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                      <i className="fa-solid fa-magnifying-glass-plus text-white text-2xl"></i>
                    </div>
                  </div>
                ))}
              </div>

              {getFilteredImages().length === 0 && (
                <div className="py-24 text-center">
                  <p className="text-brand-text-muted font-medium">Ingen billeder fundet i dette galleri.</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxState.isOpen && (
        <Lightbox
          images={lightboxState.images}
          currentIndex={lightboxState.index}
          onClose={() => setLightboxState({ isOpen: false, images: [], index: 0 })}
          onNavigate={(index) => setLightboxState((prev) => ({ ...prev, index }))}
        />
      )}
    </div>
  )
}
